import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";

import { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import InfiniteScroll from "react-swr-infinite-scroll";

import truncate from "lodash/truncate";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import AnnoucementSnippet from "components/AnnouncementSnippet";
import { Loader } from "components/customElements/loader";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Modal from "components/Modal";
import useNotification from "components/Notification/useNotification";
import { SK_CHANNEL } from "hooks/useActionCable";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useChannel } from "hooks/useChannel";
import { ApiEndpoint, baseFetcher, fetchApi } from "utils/baseFetcher";
import { convertCamelcaseObject } from "utils/common";
import shortSecurity from "utils/shortSecurity";

import styles from "./style.module.scss";
import SummaryModalContent from "./SummaryModalContent";

const FilingModal = dynamic(() => import("components/FilingModal"));

type EntityPageFilingsListProps = {};

export const entityFilingsUrl = (
  slug: string,
  pageIndex: number,
  filterTxt?: string,
  options?: { isFocused?: boolean }
) => {
  const url = new ApiEndpoint(
    `/api/v2/entities/${slug}/exchange-announcements`
  );
  url.searchParams.append(
    "include",
    "entity,locker-items,corporate-attachments,summarizations"
  );
  url.searchParams.append("page[size]", "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", "-release-time");

  if (filterTxt) {
    url.searchParams.append("filter[fulltext]", filterTxt);
  }

  if (options?.isFocused) {
    url.searchParams.append("filter[high-priority]", "true");
  }

  return url.href;
};

const sharedFilingUrl = (id: string) => {
  const url = new ApiEndpoint(`/api/v2/exchange-announcements/${id}`);
  url.searchParams.append(
    "include",
    "entity,locker-items,corporate-attachments,summarizations"
  );

  return url.href;
};

const EntityPageFilingsList = ({}: EntityPageFilingsListProps) => {
  const router = useRouter();
  const { setNotification } = useNotification();
  const { accountId } = useAuthentication();
  const slug = router.query.slug as string;
  const itemId = router.query.item as string;
  const summarizationId = router.query.summarization as string;

  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  const [latestHovered, setLatestHovered] = useState(false);

  useEffect(() => {
    if (filterTxtValue?.length >= 3) {
      setFilterTxt(filterTxtValue);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [filterTxtValue]);

  const [isFocused] = useToggle(false);

  const [pendingResourceId, setPendingResourceId] = useState<string | null>(
    null
  );
  const [pendingSource, setPendingSource] = useState<
    "button" | "snippet" | null
  >(null);
  const [summaryModal, setSummaryModal] = useState<{
    isOpen: boolean;
    content: any;
    item: any;
  } | null>(null);

  const { data: sharedFiling } = useSWR(
    itemId && summarizationId ? sharedFilingUrl(itemId) : null,
    baseFetcher
  );

  useEffect(() => {
    if (!itemId || !summarizationId || !sharedFiling) return;

    const matched = (sharedFiling.summarizations ?? []).find(
      (summarization: any) =>
        String(summarization.id) === String(summarizationId)
    );

    if (!matched) return;

    setSummaryModal({
      isOpen: true,
      content: [matched],
      item: sharedFiling,
    });
  }, [itemId, summarizationId, sharedFiling]);

  const { data, mutate, isValidating, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;

      return entityFilingsUrl(slug, pageIndex + 1, filterTxt, { isFocused });
    },
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
    }
  );

  const hasMore = data?.[size - 1]?.meta?.totalPages > size;
  const isEmpty = !data?.[0]?.data?.length;

  useEffect(() => {
    if (!hasMore) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstFiling = data?.[0]?.data?.[0];
  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Announcements - Filings`;
  const description = truncate(
    `Latest Exchange Filings on ${name} (${entity?.uniqueTicker})${
      firstFiling ? ` - ${firstFiling.title}` : ""
    }`,
    {
      length: 155,
      separator: /[/\- ]/,
    }
  );

  useChannel({
    key: "FilingsSummarization",
    name: `filing-sumarization`,
    channel: {
      channel: SK_CHANNEL,
      room: `smart-summarization`,
    },
    onReceived: async (message: any) => {
      console.log(message);
      if (message.requesting_account_id !== accountId) {
        mutate();
        return;
      }

      if (message.event === "summarization-generated") {
        setPendingResourceId(null);
        setPendingSource(null);
        const allFilings = data?.flatMap((page: any) => page?.data ?? []) ?? [];
        const matchedItem = allFilings.find(
          (f: any) => String(f.id) === String(message.resource_id)
        );
        const summary = convertCamelcaseObject(message.content);

        mutate();

        if (summary.documentTitle === "N/A") {
          setNotification(
            "We are unable to summarise this content.\nClick on the Attachment to access the underlying filing.",
            "error"
          );
          return;
        }

        setSummaryModal({
          isOpen: true,
          content: {
            ...summary,
            sourceUrl: message?.source_url,
          },
          item: matchedItem,
        });
      } else if (message.event === "summarization-failed") {
        setNotification(
          "We are unable to summarise this content.\nClick on the Attachment to access the underlying filing.",
          "error"
        );
        setPendingResourceId(null);
        setPendingSource(null);
      }
    },
  });

  const isPendingLatest =
    !!firstFiling && pendingResourceId === String(firstFiling.id);
  const latestHasSummary = !!firstFiling?.summarizations?.length;

  const handleSummarizeLatest = useCallback(async () => {
    if (!firstFiling) return;

    const sourceUrl =
      firstFiling.attachment?.[0] ||
      firstFiling.corporateAttachments?.[0]?.source;

    if (latestHasSummary) {
      const summary = firstFiling.summarizations.find(
        (summarization: any) => summarization.sourceUrl == sourceUrl
      );
      const content = summary?.content;
      if (content.documentTitle === "N/A") {
        setNotification("We are anable to summarize this content", "error");
        return;
      }
      setSummaryModal({
        isOpen: true,
        content: firstFiling.summarizations,
        item: firstFiling,
      });
      return;
    }

    setPendingResourceId(String(firstFiling.id));
    setPendingSource("button");

    try {
      await fetchApi("/api/v3/summarize", {
        method: "POST",
        body: JSON.stringify({
          resource_type: "ExchangeAnnouncement",
          resource_id: firstFiling.id,
          source_url: sourceUrl,
        }),
      });
    } catch (err) {
      console.debug(err);
    }
  }, [firstFiling, latestHasSummary, setNotification]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <div className={styles.container}>
        <div
          className={twMerge(
            styles.filter,
            "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center md:justify-between"
          )}
        >
          <div className="flex items-center">
            <span
              className={twMerge(styles.refresh, "hidden md:block")}
              onClick={() => mutate()}
            >
              Refresh
            </span>
            <div
              className={twMerge(
                styles.filterBox,
                "ml-0 md:ml-[30px] py-[5px] mr-[10px] w-full"
              )}
            >
              <FontAwesomeIcon icon={faSearch} />
              <OverlayTrigger
                placement="bottom"
                overlay={
                  filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
                }
              >
                <input
                  value={filterTxtValue}
                  autoComplete="false"
                  autoCapitalize="false"
                  onChange={(e) => setFilterTxtValue(e.target.value)}
                  aria-label="search"
                  placeholder="Search"
                  className="w-full md:max-w-[240px]"
                />
              </OverlayTrigger>
            </div>
          </div>
          {!!firstFiling &&
            firstFiling.entity.supportSummarization &&
            (!isPendingLatest || pendingSource === "button") && (
              <button
                className={twMerge(
                  "flex items-center gap-[6px] border-0 px-[14px] py-[6px] rounded-full text-ink-pink hover:text-white text-[13px] font-medium whitespace-nowrap shrink-0 transition-opacity",
                  isPendingLatest
                    ? "opacity-70 text-white bg-ink-pink cursor-not-allowed"
                    : "border border-solid border-ink-pink bg-white hover:bg-ink-pink"
                )}
                onClick={handleSummarizeLatest}
                disabled={isPendingLatest}
                onMouseEnter={() => setLatestHovered(true)}
                onMouseLeave={() => setLatestHovered(false)}
              >
                <Image
                  src={
                    latestHovered || isPendingLatest
                      ? "https://assets.smartkarma.com/icons/sparkle-white.svg"
                      : "https://assets.smartkarma.com/icons/sparkle.svg"
                  }
                  alt="Sparkle"
                  width={15}
                  height={15}
                />
                {isPendingLatest ? "Summarising..." : "Summarise Latest Filing"}
              </button>
            )}
        </div>
        <div className={styles.list}>
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size, setSize }}
            isReachingEnd={!hasMore}
            endingIndicator={
              isValidating ? (
                <Loader numberOfItems={5} />
              ) : isEmpty ? (
                <EmptyState
                  type={filterTxt ? "search" : "filing"}
                  query={filterTxt}
                  onClearClick={() => setFilterTxtValue("")}
                  entity={entity}
                />
              ) : (
                <div className={styles.endOfList}>No more filings</div>
              )
            }
          >
            {(pageData: any) => {
              if (!pageData) return null;

              return pageData?.data?.map?.((filing: any) => (
                <AnnoucementSnippet
                  key={filing.id}
                  item={filing}
                  isPendingSummarization={
                    String(filing.id) === pendingResourceId
                  }
                  shouldHideSummarizeButton={
                    firstFiling.id === String(filing.id) &&
                    pendingSource === "button"
                  }
                  onStartSummarization={(resourceId: string) => {
                    setPendingResourceId(resourceId);
                    setPendingSource("snippet");
                  }}
                  onViewSummary={(item: any, summarizations: any[]) =>
                    setSummaryModal({
                      isOpen: true,
                      content: summarizations,
                      item,
                    })
                  }
                />
              ));
            }}
          </InfiniteScroll>
        </div>
      </div>
      {itemId && !summarizationId && <FilingModal id={itemId} />}
      <Modal
        isOpen={!!summaryModal?.isOpen}
        onClose={() => setSummaryModal(null)}
        hideCloseCTA
        className="md:!w-[720px] !max-w-none !overflow-hidden !rounded-[8px] md:!p-0 md:!my-8 md:!h-[calc(100dvh-64px)] md:!max-h-[calc(100dvh-64px)]"
      >
        <SummaryModalContent
          content={summaryModal?.content}
          item={summaryModal?.item}
          onClose={() => setSummaryModal(null)}
        />
      </Modal>
    </>
  );
};

export default EntityPageFilingsList;
